import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import StarIcon from '@mui/icons-material/Star';
import axios from '../api/axios';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    specialist: '',
    privacyConsent: false, // Должно быть boolean
  });
  const [customFields, setCustomFields] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFormFields();
    fetchSpecialists();
  }, []);

  const fetchFormFields = async () => {
    try {
      console.log('Запрос кастомных полей...');
      const response = await axios.get('/form-fields/public');
      console.log('Ответ сервера:', response);
      console.log('Загруженные кастомные поля:', response.data);
      
      const fields = Array.isArray(response.data) ? response.data : [];
      console.log('Количество полей:', fields.length);
      
      setCustomFields(fields);
      
      // Инициализируем значения для кастомных полей
      const initialCustomValues = {};
      fields.forEach(field => {
        if (field.name) {
          initialCustomValues[field.name] = '';
        }
      });
      setFormData(prev => ({ ...prev, ...initialCustomValues }));
    } catch (error) {
      console.error('Ошибка загрузки кастомных полей:', error);
      console.error('Детали ошибки:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      setCustomFields([]);
    }
  };

  const fetchSpecialists = async () => {
    try {
      const response = await axios.get('/specialists/public');
      setSpecialists(response.data);
    } catch (error) {
      console.error('Error fetching specialists:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Начало отправки формы...');
    console.log('📋 Данные формы:', formData);

    if (!formData.privacyConsent) {
      console.log('❌ Согласие не дано');
      setSnackbar({
        open: true,
        message: 'Необходимо согласие на обработку персональных данных',
        severity: 'error',
      });
      return;
    }

    setLoading(true);

    try {
      // Собираем кастомные поля
      const customFieldsData = {};
      customFields.forEach(field => {
        if (formData[field.name]) {
          customFieldsData[field.name] = formData[field.name];
        }
      });

      // Берем значения из кастомных полей или стандартных полей формы
      const dataToSend = {
        name: formData.user_name || formData.name || customFieldsData.user_name || 'Не указано',
        email: formData.email || customFieldsData.email || 'not-provided@example.com',
        phone: formData.phone || customFieldsData.phone || '',
        message: formData.message || customFieldsData.message || '',
        privacyConsent: formData.privacyConsent,
        customFields: customFieldsData,
      };

      await axios.post('/requests', dataToSend);
      
      console.log('✅ Заявка успешно отправлена!');
      
      setSnackbar({
        open: true,
        message: 'Ваша заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.',
        severity: 'success',
      });
      
      console.log('✅ Snackbar установлен:', { open: true, severity: 'success' });
      
      // Сброс формы
      const resetData = {
        name: '',
        email: '',
        phone: '',
        message: '',
        specialist: '',
        privacyConsent: false,
      };
      customFields.forEach(field => {
        resetData[field.name] = '';
      });
      setFormData(resetData);
    } catch (error) {
      console.error('❌ Ошибка при отправке заявки:', error);
      
      setSnackbar({
        open: true,
        message: 'Произошла ошибка при отправке заявки. Попробуйте снова.',
        severity: 'error',
      });
      
      console.log('❌ Snackbar установлен:', { open: true, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (!field || !field.name) {
      console.error('Некорректное поле:', field);
      return null;
    }
    
    const commonProps = {
      fullWidth: true,
      label: field.label || field.name,
      name: field.name,
      value: formData[field.name] || '',
      onChange: handleChange,
      required: field.required || false,
      margin: 'normal',
      placeholder: field.placeholder || '',
    };

    switch (field.fieldType) {
      case 'textarea':
        return <TextField {...commonProps} multiline rows={4} key={field._id} />;
      case 'select':
        return (
          <TextField {...commonProps} select key={field._id}>
            <MenuItem value="">Выберите...</MenuItem>
            {field.options?.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'email':
        return <TextField {...commonProps} type="email" key={field._id} />;
      case 'phone':
        return <TextField {...commonProps} type="tel" key={field._id} />;
      case 'number':
        return <TextField {...commonProps} type="number" key={field._id} />;
      case 'date':
        return <TextField {...commonProps} type="date" InputLabelProps={{ shrink: true }} key={field._id} />;
      default:
        return <TextField {...commonProps} key={field._id} />;
    }
  };

  return (
    <Box id="request" sx={{ py: 8, backgroundColor: 'white' }}>
      <Container maxWidth="xl">

        <Grid container spacing={4}>
          {/* Форма заявки - занимает 8 колонок (2/3 ширины) */}
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Оставить заявку
              </Typography>
              <form onSubmit={handleSubmit}>    
                {/* Кастомные поля */}
                {customFields && customFields.length > 0 ? (
                  customFields.map(field => {
                    console.log('Рендеринг поля:', field);
                    return renderField(field);
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Нет доступных полей формы. Добавьте поля в админ-панели и убедитесь, что они активны.
                  </Typography>
                )}

             

                {/* Чекбокс согласия */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="privacyConsent"
                      checked={formData.privacyConsent}
                      onChange={handleChange}
                      disableRipple
                      sx={{
                        animation: !formData.privacyConsent ? 'glow 2s ease-in-out infinite' : 'none',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '@keyframes glow': {
                          '0%, 100%': {
                            filter: 'drop-shadow(0 0 8px rgba(25, 118, 210, 1)) drop-shadow(0 0 4px rgba(25, 118, 210, 0.8))',
                          },
                          '50%': {
                            filter: 'drop-shadow(0 0 15px rgba(25, 118, 210, 1)) drop-shadow(0 0 8px rgba(25, 118, 210, 1))',
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Я согласен(а) на{' '}
                      <Link href="/privacy-policy" target="_blank" underline="hover">
                        обработку персональных данных
                      </Link>
                      {' '}<span>*</span>
                    </Typography>
                  }
                  sx={{ mt: 2 }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !formData.privacyConsent}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Отправка...' : 'Отправить заявку'}
                </Button>
              </form>
            </Paper>
          </Grid>

          {/* Опросы качества - занимают 4 колонки (1/3 ширины) */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6,
                  },
                }}
              >
                <Box>
                  <ThumbUpIcon sx={{ fontSize: 50, color: '#ff9800', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                  Оцените качество услуг нашего консультационного центра
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  href="https://quick.apkpro.ru/poll/21421"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  Пройти опрос
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ 
          zIndex: 9999,
          mt: 10, // Увеличен отступ сверху, чтобы не накладывался на меню
          mr: 2
        }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ 
            width: '100%',
            minWidth: '300px',
            boxShadow: 3
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
